/* @flow strict */

/* eslint-disable camelcase */

import * as React from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import _ from 'lodash';
import RangeCalendar from '/client/src/components/calendar/range';
import moment from 'moment';
import { GET } from '/client/src/api/';
import { mockData } from './mock';
import { currency } from '/client/src/constants/';

type TooltipPopupType = {
  label?: string,
  payload?: [],
  currencySign?: string
};

const TooltipPopup = (props: TooltipPopupType): React.Element<'div'> => {
  if (!props || !props.payload) {
    return null;
  }

  const { payload, currencySign } = props;
  const _payload = _.isArray(payload) ? payload[0] : payload;

  return (
    <div className='TooltipPopup'>
      <h4 className='TooltipPopupTitle'>{props.label || ''}</h4>

      <ul className="TooltipPopupBody">
        <li className="TooltipPopupBodyItem">
          <span>Payout</span>
          <span>{currencySign}{_payload ? _payload.payload.uv : ''}</span>
        </li>
        <li className="TooltipPopupBodyItem">
          <span>Tickets Purchased</span>
          <span>{_payload ? _payload.payload.pv : ''}</span>
        </li>
      </ul>

      <ul className="TooltipPopupBody">
        <li className="TooltipPopupBodyItem">
          <span>Refunds</span>
          <span>{currencySign}{_payload ? _payload.payload.rf : ''}</span>
        </li>
        <li className="TooltipPopupBodyItem">
          <span>Tickets Refunds</span>
          <span>{_payload ? _payload.payload.amt : ''}</span>
        </li>
      </ul>
    </div>
  );
};

type PropsType = {
  eventId: string | number,
  tickets: [],
  isNoStatistic: boolean,
  currencySign: string
};

const EventPageDetailStatsTicketSales = ({ eventId, tickets, isNoStatistic, currencySign }: PropsType): React.Element<'div'> => {
  const chartDateFormat = 'YYYY-MM-DD';
  const getMinMaxDate = (): [] => {
    const minMaxDate = [];

    _.map(tickets, (t: {}) => {
      minMaxDate.push(t.sale_begin_time_utc);
      minMaxDate.push(t.sale_end_time_utc);
    });

    const moments = minMaxDate.map(d => moment(d));
    const maxDate = moment.max(moments);
    const minDate = moment.min(moments);

    return { minDate, maxDate };
  };

  const [ isLoading, setIsLoading ] = React.useState(true);
  const { minDate, maxDate } = getMinMaxDate();
  const [ endTime, setEndDate ] = React.useState((): void => {
    const date = maxDate.isBefore(moment()) ? maxDate : moment();

    return date.format(chartDateFormat);
  });
  const [ startTime, setStartDate ] = React.useState(() => {
    const date = moment(endTime).isBefore(moment()) ? endTime : moment();

    return moment(date).subtract(6, 'days').format(chartDateFormat);
  });

  const [eventTransactionStatistics, setEventTransactionStatistics ] = React.useState(null);

  // eslint-disable-next-line
  const salesData = (name: any, uv = 0, pv = 0, amt = 0, lv = 0, rf = 0) => {
    return { name, uv, pv, amt, lv, rf };
  };

  const setEndTime = (value: any): void => setEndDate(moment(value).format(chartDateFormat));

  const setStartTime = (value: any): void => setStartDate(moment(value).format(chartDateFormat));

  const fetchData = async () => {
    setIsLoading(true);


    try {
     const { result } = await GET(
        // eslint-disable-next-line
        `/api/v1/1/statistics/events/${eventId}/transaction?begin_timestamp_utc=${moment(startTime).unix()}&end_timestamp_utc=${moment(endTime).endOf("day").unix()}&timezone_offset=${-moment().utcOffset()}`
      );

      const _data = _.get(result, 'event_transaction_statistics', null);

      const reformatDataToChart = (data: {}, currentCountUnit: string, formatDate: string) => {
        const currentCount = ~moment(startTime).diff(moment(endTime), currentCountUnit) + 1;
        const monthMode = currentCount > 30;

        let transformedData = [];

        let currentData;

        for (let i = currentCount; i >= 0; i--) {
          transformedData.push(salesData(moment(endTime).subtract(i, currentCountUnit).format(formatDate)));
        }

        _.map(data, (_d: {}): {} => {
          const day = moment(_d.stat_day).format(formatDate);

          currentData = salesData(
            day,
            _d.merchant_revenue / 100,
            _d.sold_quantity,
            _d.return_quantity,
            _d.sold_quantity,
            _d.merchant_revenue_refund / 100
          );

          transformedData.find((e: any, index: number) => {
            if (e.name === day)  transformedData.splice(index, 1, currentData);
          });
        });

        if (monthMode) {
          transformedData = splitDataByWeek(transformedData);
        }
        setEventTransactionStatistics(transformedData);
        setIsLoading(false);
      };

      const splitDataByWeek = (data): any => {
        const perChunk = 7; // items per chunk

        const chunked = data.reduce((resultArray, item, index): any => {
          const chunkIndex = Math.floor(index / perChunk);

          if (!resultArray[chunkIndex]) {
            resultArray[chunkIndex] = []; // start a new chunk
          }

          resultArray[chunkIndex].push(item);

          return resultArray;
        }, []);

         const result = chunked.map((el): any =>
          el.reduce((prev, item, index, arr): any => {
            return {
              amt: prev.amt + item.amt,
              lv: prev.lv + item.lv,
              name: `${arr[0].name} - ${arr[arr.length - 1].name}`,
              pv: prev.pv + item.pv,
              rf: prev.rf + item.rf,
              uv: prev.uv + item.uv
            };
          })
         );

         return result;
      };

      reformatDataToChart(_data, 'days', 'MMM DD');
    } catch (e) {
      console.log(e);
      console.error('Cant get ticket sales statistic');
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (isNoStatistic) {
      setEventTransactionStatistics(mockData.TicketSales);
      setIsLoading(false);
    } else {
      fetchData();
    }
  }, [startTime, endTime]);

  return (
    <div id="net-payout" className="panel panel-chart panel-transparent">
          <div className="panel-heading">
            <span>Ticket Sales</span>

            <div className="pull-right chart1-datetime">
              <RangeCalendar
                end={endTime}
                start={startTime}
                startCb={setStartTime}
                endCb={setEndTime}
              />
            </div>
          </div>
          <div className="panel-body no-padding">
            <div className="chart1">
              <TooltipPopup label='test' currencySign={currencySign} />
              <div style={{ width: '100%', height: 300 }}>
                {!isLoading ?
                  <ResponsiveContainer>
                    <ComposedChart
                      data={eventTransactionStatistics}
                      margin={{
                        top: 15,
                        right: 10,
                        bottom: 0,
                        left: 10
                      }}
                    >
                      <defs>
                        <linearGradient id="colorPv" x1="0" y1="0" x2="0"
                          y2="1"
                        >
                          <stop offset="5%" stopColor="#648fdb"/>
                          <stop offset="95%" stopColor="#74b7d0"/>
                        </linearGradient>
                        <linearGradient id="colorUv" x1="0" y1="0" x2="0"
                          y2="1"
                        >
                          <stop offset="5%" stopColor="#ffcb93"/>
                          <stop offset="95%" stopColor="#ff697f"/>
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="name"
                        strokeWidth={1}
                        tick={null}
                        padding={{ left: 30, right: 30 }}
                        domain={[startTime, endTime]}
                        tickFormatter = {(e) => moment(e).format('HH:mm Do')}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        stroke="#ff697f"
                        dataKey="uv"
                        tickFormatter={(e: any): void => `${currencySign}${e}`}
                        mirror
                        allowDataOverflow
                      />
                      <YAxis
                        yAxisId="left"
                        stroke='#648fdb'
                        dataKey="pv"
                        mirror
                      />
                      <Tooltip
                        content={<TooltipPopup currencySign={currencySign} />}
                      />
                        <Bar
                          yAxisId="right"
                          dataKey='uv'
                          stackId="a"
                          barSize={30}
                          fill="url(#colorUv)"
                        />
                      {/* <Bar*/}
                      {/*  yAxisId="left"*/}
                      {/*  dataKey="pv"*/}
                      {/*  stackId="a"*/}
                      {/*  barSize={30}*/}
                      {/*  fill="url(#colorPv)"*/}
                      {/* />*/}
                      <Line yAxisId="left" dataKey='lv' stroke='#37b7f2' strokeWidth={3} />
                    </ComposedChart>
                  </ResponsiveContainer>
                :
                  <div className='csspinner' style={{ left: '0', top: '50%' }} />
                }
              </div>
            </div>
          </div>
    </div>
  );
};

export default EventPageDetailStatsTicketSales;
